// The single seam through which the SPA talks to the v2 registrar server.
//
// Unlike the reference app (which injected a Firebase Bearer token), v2 identity
// rides the same-origin `nori_session` cookie (sent automatically) and the
// server's own stored PAT; there is no per-request bearer to attach here. The
// wrapper asks for JSON (the server is a JSON API and never returns HTML),
// serializes object bodies, maps a 204 to `undefined`, and turns any non-2xx
// into an `ApiError` carrying the parsed error envelope.

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(message: string, status: number, statusText: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function messageOf(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const err = (body as { error?: { message?: unknown } }).error;
    if (err && typeof err.message === 'string') {
      return err.message;
    }
  }
  return fallback;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    signal: options.signal,
  };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(path, init);

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      messageOf(body, `${response.status} ${response.statusText}`),
      response.status,
      response.statusText,
      body,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
